import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, MenuItem,
    CircularProgress, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Checkbox, Chip, FormControl, InputLabel,
    Select, Divider, Card, CardContent
} from '@mui/material';
import { Print, Download, Visibility } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import api from '../services/api';

const ID_CARD_FORMATS = [
    { value: 'portrait', label: 'Portrait', description: 'Standard portrait card' },
    { value: 'portrait1', label: 'Portrait 1', description: 'With photo left' },
    { value: 'portrait2', label: 'Portrait 2', description: 'With photo right' },
    { value: 'portrait3', label: 'Portrait 3', description: 'Compact' },
    { value: 'portrait4', label: 'Portrait 4', description: 'With QR' },
    { value: 'portrait5', label: 'Portrait 5', description: 'Modern' },
    { value: 'portrait6', label: 'Portrait 6', description: 'Minimal' },
    { value: 'landscape', label: 'Landscape', description: 'Horizontal layout' },
    { value: 'landscape1', label: 'Landscape 1', description: 'With details' },
    { value: 'landscape2', label: 'Landscape 2', description: 'Two-sided' },
    { value: 'landscape3', label: 'Landscape 3', description: 'Premium' }
];

const PrintIdCard = () => {
    const [classes, setClasses] = useState([]);
    const [sections, setSections] = useState([]);
    const [academicYears, setAcademicYears] = useState([]);
    const [students, setStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [selectedFormat, setSelectedFormat] = useState('portrait');
    const [loading, setLoading] = useState(false);
    const [idCardData, setIdCardData] = useState(null);

    const [formData, setFormData] = useState({
        academicYearId: '',
        classId: '',
        sectionId: '',
        regNo: '',
        fromDate: '',
        toDate: '',
        formSubmission: ''
    });

    const componentRef = useRef();
    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        pageStyle: `
            @page { 
                size: ${selectedFormat.includes('landscape') ? 'landscape' : 'portrait'}; 
                margin: 10mm;
            }
            @media print {
                body { -webkit-print-color-adjust: exact; }
            }
        `
    });

    useEffect(() => {
        fetchClasses();
        fetchAcademicYears();
    }, []);

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            setClasses(res.data.data || []);
        } catch (error) {
            console.error("Failed to fetch classes");
        }
    };

    const fetchAcademicYears = async () => {
        try {
            const res = await api.get('/academic-years');
            setAcademicYears(res.data.data || []);
            if (res.data.data?.length > 0) {
                const current = res.data.data.find(y => y.isCurrent) || res.data.data[0];
                setFormData(prev => ({ ...prev, academicYearId: current.id }));
            }
        } catch (error) {
            console.error("Failed to fetch academic years");
        }
    };

    const handleClassChange = async (e) => {
        const classId = e.target.value;
        setFormData({ ...formData, classId, sectionId: '' });
        try {
            const res = await api.get(`/classes/${classId}/sections`);
            setSections(res.data.data || []);
        } catch (error) {
            console.error("Failed to fetch sections");
        }
    };

    const handleSearch = async () => {
        setLoading(true);
        try {
            const params = {
                classId: formData.classId,
                sectionId: formData.sectionId,
                academicYearId: formData.academicYearId,
                ...(formData.regNo && { regNo: formData.regNo }),
                ...(formData.fromDate && { fromDate: formData.fromDate }),
                ...(formData.toDate && { toDate: formData.toDate })
            };
            const res = await api.get('/idcards/students', { params });
            setStudents(res.data.data || []);
            setSelectedStudents([]);
        } catch (error) {
            toast.error("Failed to fetch students");
            // Fallback to regular student list
            try {
                const res = await api.get('/students', {
                    params: { class: formData.classId, section: formData.sectionId, status: 'active' }
                });
                const formatted = (res.data.data || []).map(s => ({
                    id: s.id,
                    regNo: s.admissionNumber,
                    rollNo: s.rollNumber,
                    name: `${s.user?.firstName || ''} ${s.user?.lastName || ''}`.trim(),
                    class: s.class?.name,
                    division: s.section?.name,
                    dob: s.user?.dateOfBirth,
                    gender: s.user?.gender,
                    bloodGroup: s.bloodGroup,
                    mobile: s.mobile,
                    address: s.address,
                    photo: s.user?.profileImage || s.photo,
                    academicYear: s.academicYear?.year,
                    caste: s.caste,
                    category: s.category
                }));
                setStudents(formatted);
            } catch (err) {
                setStudents([]);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedStudents(students.map(s => s.id));
        } else {
            setSelectedStudents([]);
        }
    };

    const handleSelectStudent = (id) => {
        setSelectedStudents(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const handleGenerateCards = async () => {
        if (selectedStudents.length === 0) {
            toast.warning("Please select at least one student");
            return;
        }
        setLoading(true);
        try {
            const res = await api.post('/idcards/generate', {
                studentIds: selectedStudents,
                format: selectedFormat,
                cardType: 'student'
            });
            setIdCardData(res.data.data);
            toast.success(`Generated ${res.data.count} ID cards`);
        } catch (error) {
            // Generate locally if API fails
            const selected = students.filter(s => selectedStudents.includes(s.id));
            setIdCardData(selected.map(s => ({ type: 'student', format: selectedFormat, data: s })));
            toast.success(`Generated ${selected.length} ID cards`);
        } finally {
            setLoading(false);
        }
    };

    const renderIdCard = (student, index) => {
        const isLandscape = selectedFormat.includes('landscape');
        const cardStyle = {
            width: isLandscape ? 337 : 214,  // 85.6mm x 53.98mm or 53.98mm x 85.6mm at 96dpi
            height: isLandscape ? 214 : 337,
            border: '2px solid #1565c0',
            borderRadius: 8,
            background: 'linear-gradient(180deg, #1565c0 0%, #1976d2 30%, #fff 30%)',
            overflow: 'hidden',
            pageBreakAfter: (index + 1) % 4 === 0 ? 'always' : 'auto',
            display: 'inline-block',
            margin: 8
        };

        return (
            <Box key={student.id || index} sx={cardStyle}>
                {/* Header */}
                <Box sx={{
                    background: 'linear-gradient(180deg, #1565c0, #1976d2)',
                    color: 'white',
                    p: 1,
                    textAlign: 'center',
                    height: isLandscape ? 50 : 70
                }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: 10 }}>
                        VIDYA PRATISHTHAN'S
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: 11 }}>
                        SUPE ENGLISH MEDIUM SCHOOL
                    </Typography>
                </Box>

                {/* Body */}
                <Box sx={{ p: 1, display: 'flex', flexDirection: isLandscape ? 'row' : 'column', gap: 1 }}>
                    {/* Photo */}
                    <Box sx={{
                        width: isLandscape ? 60 : 80,
                        height: isLandscape ? 70 : 90,
                        border: '1px solid #1565c0',
                        borderRadius: 1,
                        background: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: isLandscape ? 0 : 'auto',
                        mt: isLandscape ? 0 : 1
                    }}>
                        {student.data?.photo ? (
                            <img src={student.data.photo} alt="Student" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
                        ) : (
                            <Typography variant="caption" color="text.secondary">Photo</Typography>
                        )}
                    </Box>

                    {/* Details */}
                    <Box sx={{ flex: 1, fontSize: 9 }}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: 11, color: '#1565c0', mb: 0.5 }}>
                            {student.data?.name || student.name}
                        </Typography>
                        <Box sx={{ display: 'grid', gap: 0.3 }}>
                            <Typography variant="caption"><b>Reg No:</b> {student.data?.regNo || student.regNo}</Typography>
                            <Typography variant="caption"><b>Class:</b> {student.data?.class || student.class} - {student.data?.div || student.data?.division || student.division}</Typography>
                            <Typography variant="caption"><b>DOB:</b> {student.data?.dob || student.dob}</Typography>
                            <Typography variant="caption"><b>Blood Group:</b> {student.data?.bloodGroup || student.bloodGroup || '-'}</Typography>
                            <Typography variant="caption"><b>Mobile:</b> {student.data?.mobile || student.mobile || '-'}</Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Footer */}
                <Box sx={{
                    background: '#1565c0',
                    color: 'white',
                    p: 0.5,
                    textAlign: 'center',
                    position: 'absolute',
                    bottom: 0,
                    width: '100%'
                }}>
                    <Typography variant="caption" sx={{ fontSize: 8 }}>
                        {student.data?.academicYear || student.academicYear || '2025-2026'}
                    </Typography>
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Print ID Cards</Typography>

            {/* Search Form */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Academic Year</InputLabel>
                            <Select
                                value={formData.academicYearId}
                                label="Academic Year"
                                onChange={e => setFormData({ ...formData, academicYearId: e.target.value })}
                            >
                                {academicYears.map(y => (
                                    <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Class</InputLabel>
                            <Select value={formData.classId} label="Class" onChange={handleClassChange}>
                                {classes.map(cls => (
                                    <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Division</InputLabel>
                            <Select
                                value={formData.sectionId}
                                label="Division"
                                onChange={e => setFormData({ ...formData, sectionId: e.target.value })}
                                disabled={!formData.classId}
                            >
                                {sections.map(sec => (
                                    <MenuItem key={sec.id} value={sec.id}>{sec.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Reg No"
                            value={formData.regNo}
                            onChange={e => setFormData({ ...formData, regNo: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="From Date"
                            type="date"
                            value={formData.fromDate}
                            onChange={e => setFormData({ ...formData, fromDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="To Date"
                            type="date"
                            value={formData.toDate}
                            onChange={e => setFormData({ ...formData, toDate: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button variant="contained" onClick={handleSearch} disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : 'Search'}
                    </Button>
                    <Button variant="outlined" onClick={() => {
                        setFormData({ academicYearId: formData.academicYearId, classId: '', sectionId: '', regNo: '', fromDate: '', toDate: '', formSubmission: '' });
                        setStudents([]);
                        setSelectedStudents([]);
                    }}>
                        Reset
                    </Button>
                </Box>
            </Paper>

            {/* Format Selection */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Select ID Card Format:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ID_CARD_FORMATS.map(fmt => (
                        <Chip
                            key={fmt.value}
                            label={fmt.label}
                            onClick={() => setSelectedFormat(fmt.value)}
                            color={selectedFormat === fmt.value ? 'primary' : 'default'}
                            variant={selectedFormat === fmt.value ? 'filled' : 'outlined'}
                        />
                    ))}
                </Box>
            </Paper>

            {/* Student Selection Table */}
            {students.length > 0 && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2">
                            Select Students ({selectedStudents.length} of {students.length} selected)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                startIcon={<Visibility />}
                                onClick={handleGenerateCards}
                                disabled={selectedStudents.length === 0}
                            >
                                Generate Preview
                            </Button>
                            {idCardData && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<Print />}
                                    onClick={handlePrint}
                                >
                                    Print Cards
                                </Button>
                            )}
                        </Box>
                    </Box>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedStudents.length === students.length && students.length > 0}
                                            indeterminate={selectedStudents.length > 0 && selectedStudents.length < students.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell>Reg No</TableCell>
                                    <TableCell>Roll No</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Class</TableCell>
                                    <TableCell>Division</TableCell>
                                    <TableCell>Gender</TableCell>
                                    <TableCell>DOB</TableCell>
                                    <TableCell>Blood Group</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {students.map(std => (
                                    <TableRow
                                        key={std.id}
                                        hover
                                        selected={selectedStudents.includes(std.id)}
                                        onClick={() => handleSelectStudent(std.id)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={selectedStudents.includes(std.id)} />
                                        </TableCell>
                                        <TableCell>{std.regNo}</TableCell>
                                        <TableCell>{std.rollNo}</TableCell>
                                        <TableCell>{std.name}</TableCell>
                                        <TableCell>{std.class}</TableCell>
                                        <TableCell>{std.division}</TableCell>
                                        <TableCell>{std.gender}</TableCell>
                                        <TableCell>{std.dob}</TableCell>
                                        <TableCell>{std.bloodGroup || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}

            {/* ID Card Preview */}
            {idCardData && (
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle2">ID Card Preview - {selectedFormat.toUpperCase()}</Typography>
                        <Button variant="contained" color="primary" startIcon={<Print />} onClick={handlePrint}>
                            Print All ({idCardData.length} cards)
                        </Button>
                    </Box>
                    <Box ref={componentRef} sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        justifyContent: 'center',
                        p: 2,
                        background: '#f5f5f5',
                        minHeight: 400
                    }}>
                        {idCardData.map((card, idx) => renderIdCard(card, idx))}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default PrintIdCard;
